import type { TypographyProps } from '@tunasong/ui-lib'
import { Typography } from '@tunasong/ui-lib'
import type { ChromaSpectrum } from '@tunasong/audio-dsp'
import { shortUuid } from '@tunasong/models'
import { standardNoteNames } from '@tunasong/schemas'
import { HBox } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { AudioController } from '../engine/index.js'

export interface ChromaViewProps extends TypographyProps {
  audioController: AudioController
}

/** Performant real-time view of chroma features */
export const ChromaView: FC<ChromaViewProps> = props => {
  const { audioController, ...typographyProps } = props

  // The max level of chroma we have seen, which will corrspond to 1.0 opacity

  const id = useMemo(() => shortUuid(), [])
  const getElId = useCallback((idx: number) => `chroma-band-${id}-${idx}`, [id])
  const updateOpacity = useCallback(
    (idx: number, opacity: number) => {
      const el = document.getElementById(getElId(idx))
      if (el) {
        // Square the opacity for better contrast
        const opQ = opacity * opacity * opacity * opacity
        const op = opQ > 0.7 ? opQ : 0
        el.style.opacity = op.toString()
      }
    },
    [getElId]
  )

  const clear = useCallback(() => {
    for (let i = 0; i < 12; i++) {
      updateOpacity(i, 0.1)
    }
  }, [updateOpacity])

  const handleChroma = useCallback(
    (ev: CustomEvent<ChromaSpectrum | null>) => {
      /** If the chroma processor is not enabled we can get a null message here if other processors */
      const chroma = ev.detail

      // Clear opacity if no chroma
      if (!chroma) {
        clear()
        return
      }

      const maxVal = Math.max(...Object.values(chroma))

      for (const [idx, val] of Object.entries(chroma ?? [])) {
        const normalizedVal = val / maxVal
        updateOpacity(Number(idx), normalizedVal)
      }
    },
    [clear, updateOpacity]
  )

  useEffect(() => {
    audioController.enableDSPFeature('chroma', true)
    audioController.addFeatureListener('chroma', handleChroma)

    return () => {
      /** @todo what if others require chroma? */
      /** @todo perhaps we should automatically check if we have  */
      audioController.enableDSPFeature('chroma', false)
      audioController.removeFeatureListener('chroma', handleChroma)
    }
  }, [audioController, handleChroma])

  return (
    <HBox style={{ justifyContent: 'space-between' }}>
      {standardNoteNames.map((note, idx) => (
        <Typography
          {...typographyProps}
          id={getElId(idx)}
          key={idx}
          style={{ ...typographyProps.style, color: 'var(--tuna-palette-secondary-light)' }}
        >
          {note}
        </Typography>
      ))}
    </HBox>
  )
}

export default ChromaView
